import api from '../../utils/api'
import { useState, useEffect, useMemo } from 'react'
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa'
import { FiInbox, FiFolderPlus, FiCornerDownRight } from 'react-icons/fi'

import Button from '../../shared/components/Button/Button'
import Modal from '../../shared/modal/Modal'
import ConfirmDeleteModal from '../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal'
import FormFinanceCategory from './Form/FormFinanceCategory'
import useMyPermission from '../../hooks/useMyPermission'
import useFlashMessage from '../../hooks/useFlashMessage'

import './Finance.css'

// Lista única de categorias, compartilhada entre Contas a Pagar e a Receber — decisão explícita
// do usuário (diferente de FinanceEntry/FinanceRecurringTemplate, que são sempre um ou outro).
function Categories() {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [categories, setCategories] = useState([])
    const [load, setLoad] = useState(false)

    const [modalNewCategory, setModalNewCategory] = useState(false)
    const [modalNewSubcategory, setModalNewSubcategory] = useState(false)
    const [editingCategory, setEditingCategory] = useState(null)
    const [deletingCategory, setDeletingCategory] = useState(null)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: '' })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, 'finance_view')
    const myPermissionCreate = checkPermission(myPermission, 'finance_create')
    const myPermissionEdit = checkPermission(myPermission, 'finance_edit')
    const myPermissionDelete = checkPermission(myPermission, 'finance_delete')

    const getAll = () => {
        api.get('/finance/category/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => {
                setCategories(response.data.categories || [])
                setMyPermission(response.data.permission)
                setLoad(true)
            })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    const tree = useMemo(() => {
        const parents = categories.filter((c) => !c.parent)
        return parents.map((parent) => ({
            ...parent,
            children: categories.filter((c) => c.parent === parent._id),
        }))
    }, [categories])

    const parentOptions = useMemo(() => (
        categories.filter((c) => !c.parent).map((c) => ({ value: c._id, label: c.name }))
    ), [categories])

    async function confirmDelete() {
        let msgType = 'success'
        const data = await api.delete(`/finance/category/${deletingCategory._id}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        setDeletingCategory(null)
        if (msgType !== 'error') getAll()
    }

    if (!load) {
        return <div className="pg-loading">Carregando categorias...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Categorias</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionCreate && (
                                <Button variant="new" icon={FiFolderPlus} onClick={() => setModalNewCategory(true)}>Categoria</Button>
                            )}
                            {myPermissionCreate && (
                                <Button variant="new" icon={FiCornerDownRight} onClick={() => setModalNewSubcategory(true)} disabled={parentOptions.length === 0}>Subcategoria</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <span className="pg-count">{categories.length} {categories.length === 1 ? 'categoria' : 'categorias'}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="fin-cat-tree">
                {tree.length > 0 ? tree.map((parent) => (
                    <div key={parent._id}>
                        <div className="fin-cat-parent">
                            <span className="fin-cat-name">{parent.name}</span>
                            <div className="fin-cat-actions">
                                {myPermissionEdit && <FaPencilAlt className="shd-icon-row-edit" title="Editar" onClick={() => setEditingCategory(parent)} />}
                                {myPermissionDelete && <FaTrashAlt className="shd-icon-row-trash" title="Apagar" onClick={() => setDeletingCategory(parent)} />}
                            </div>
                        </div>
                        {parent.children.map((sub) => (
                            <div key={sub._id} className="fin-cat-sub">
                                <span>{sub.name}</span>
                                <div className="fin-cat-actions">
                                    {myPermissionEdit && <FaPencilAlt className="shd-icon-row-edit" title="Editar" onClick={() => setEditingCategory(sub)} />}
                                    {myPermissionDelete && <FaTrashAlt className="shd-icon-row-trash" title="Apagar" onClick={() => setDeletingCategory(sub)} />}
                                </div>
                            </div>
                        ))}
                    </div>
                )) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhuma categoria cadastrada</p>
                        <p className="pg-empty-subtitle">Cadastre a primeira categoria pra começar a organizar seus lançamentos.</p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNewCategory}
                changeStatus={setModalNewCategory}
                w={55}
                content={
                    <FormFinanceCategory
                        parentOptions={parentOptions}
                        btCancelAction={() => setModalNewCategory(false)}
                        handleOnSuccess={() => { setModalNewCategory(false); getAll() }}
                    />
                }
            />

            <Modal
                modalStatus={modalNewSubcategory}
                changeStatus={setModalNewSubcategory}
                w={55}
                content={
                    <FormFinanceCategory
                        parentOptions={parentOptions}
                        isSubcategory
                        btCancelAction={() => setModalNewSubcategory(false)}
                        handleOnSuccess={() => { setModalNewSubcategory(false); getAll() }}
                    />
                }
            />

            <Modal
                modalStatus={!!editingCategory}
                changeStatus={() => setEditingCategory(null)}
                w={55}
                content={
                    editingCategory && (
                        <FormFinanceCategory
                            parentOptions={parentOptions}
                            editingCategory={editingCategory}
                            btCancelAction={() => setEditingCategory(null)}
                            handleOnSuccess={() => { setEditingCategory(null); getAll() }}
                        />
                    )
                }
            />

            <ConfirmDeleteModal
                modalStatus={!!deletingCategory}
                changeStatus={() => setDeletingCategory(null)}
                onConfirm={confirmDelete}
                message="Deseja realmente apagar essa categoria?"
                itemName={deletingCategory?.name}
            />

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar o Financeiro</p>
        </div>
    )
}

export default Categories
